import classnames from 'classnames';
import React, {useEffect, useRef} from 'react';

import {Icon, IconButton, Text} from '@albert-io/atomic';
import {moveTabWithin} from 'lib/focusUtils';
import useAutoFocusReturn from 'lib/hooks/useAutoFocusReturn';
import usePrevious from 'lib/hooks/usePrevious';

import AnnotationEditor from '../AnnotationEditor/AnnotationEditor';
import {isSupplementAnchor} from '../engine/annotationAnchors';
import {NOTE_TOOLTIP_PROPS} from '../constants';
import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {focusAnnotationFallbackIfLost} from '../shared/annotationFocusFallback';
import type {QuestionNote} from './annotationNotes';
import {findNoteRow} from './AnnotationNotesPanelItem';
import AnnotationNotesPanelList from './AnnotationNotesPanelList';
import {selectIsNoteListed} from '../store/annotationSelectors';
import {useCanViewAnnotations} from '../mode/useCanViewAnnotations';
import {useIsAnnotationModeAvailable} from '../mode/useIsAnnotationModeAvailable';
import {useRegionEdit} from '../store/useRegionEdit';

const PANEL_TITLE_ID = 'annotation-notes-panel-title';

/** Fluid so the panel still fits at high zoom; panel widths have no tokens. */
const PANEL_WIDTH_CLASS = 'w-[clamp(176px,24vw,320px)]';

/** Below `xs` the open panel lays over the question instead of squeezing it (SC 1.4.10). */
const PANEL_OVERLAY_CLASS = 'max-xs:inset-y-x0 max-xs:right-x0 max-xs:z-raised max-xs:absolute';

/** The open panel's widest, which `PANEL_WIDTH_CLASS` caps at; layout math reserves this much. */
export const NOTES_PANEL_WIDTH_PX = 320;

/** The note whose editor is open, and the row its popover hangs below. */
interface PanelEdit {
  note: QuestionNote;
  row: HTMLLIElement;
}

interface AnnotationNotesPanelProps {
  /** The question whose notes are listed; undefined while one loads. */
  questionId: string | undefined;
}

/**
 * The question's notes, docked beside it rather than over it. Stays mounted
 * while closed so the width can animate; `inert` keeps its controls untabbable.
 */
export default function AnnotationNotesPanel({questionId}: AnnotationNotesPanelProps) {
  const canViewAnnotations = useCanViewAnnotations();
  const canEditAnnotations = useIsAnnotationModeAvailable();
  const [editingNote, openEditor, closeEditor] = useRegionEdit<PanelEdit>();
  const isOpen = useAnnotationModeStore((state) => state.isNotesPanelOpen);
  const focusNoteId = useAnnotationModeStore((state) => state.notesPanelFocusNoteId);
  const isReadOnly = useAnnotationModeStore((state) => state.isReadOnly);
  const closeNotesPanel = useAnnotationModeStore((state) => state.closeNotesPanel);
  const panelRef = useRef<HTMLElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const wasOpen = usePrevious(isOpen);
  // Never true on a remount: PracticeView remounts per question navigation, and
  // an already-open panel must not steal focus from the question.
  const justOpened = isOpen && wasOpen === false;
  const restoreFocus = useAutoFocusReturn(panelRef, justOpened);
  useEffect(() => {
    if (justOpened && focusNoteId) {
      findNoteRow(panelRef.current, focusNoteId)?.focus();
    }
  }, [justOpened, focusNoteId]);
  const isEditedNoteListed = useAnnotationModeStore((state) =>
    selectIsNoteListed(state, editingNote?.note.regionId, editingNote?.note.annotation.id)
  );
  const activeEdit = isEditedNoteListed ? editingNote : null;

  // Focus must leave before `inert` pins it to a disabled control. Only keyboard
  // closes then call `restoreFocus`: the indicator's peek popover opens on focus,
  // so handing it back after a mouse close would spring the peek open again.
  const close = () => {
    closeEditor();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    closeNotesPanel();
  };

  // Stable: AnnotationPopover feeds it to three global listeners.

  if (!canViewAnnotations || isReadOnly) {
    return null;
  }

  return (
    <>
      <aside
        ref={panelRef}
        aria-labelledby={PANEL_TITLE_ID}
        inert={!isOpen}
        data-testid='annotation-notes-panel'
        className={classnames(
          'shrink-0 overflow-hidden transition-[width] duration-[var(--duration-fast)] ease-linear motion-reduce:transition-none',
          PANEL_OVERLAY_CLASS,
          isOpen ? [PANEL_WIDTH_CLASS, 'max-xs:w-full'] : 'w-x0'
        )}
        onKeyDown={(event) => {
          // Held in while open, so Tab can't strand focus past the panel; Escape leaves.
          if (event.key === 'Tab') {
            event.preventDefault();
            moveTabWithin(event.currentTarget, event.shiftKey);
            return;
          }
          // preventDefault makes the document-level mode-exit Escape listener yield.
          if (event.key === 'Escape') {
            event.preventDefault();
            close();
            restoreFocus();
            focusAnnotationFallbackIfLost();
          }
        }}
      >
        {/* No border-solid: without preflight it paints 3px UA borders on unsized sides. */}
        <div className='background-subtle border-border-primary flex h-full w-full flex-col border-l'>
          <header className='gap-x1 px-x2 py-x1-half background-primary border-border-primary flex shrink-0 items-center justify-between border-b'>
            {/* Icon inside the title so it inherits the 15px size; font-normal and
                m-x0 shed the h2's UA bold and legacy margin. */}
            <Text
              as='h2'
              id={PANEL_TITLE_ID}
              size='m'
              color='primary'
              className='gap-x1 m-x0 flex items-center font-normal'
            >
              <Icon icon='notes' iconStyle='regular' />
              Notes
            </Text>
            <IconButton
              ref={closeButtonRef}
              icon='times'
              iconStyle='regular'
              label='Close notes'
              size='s'
              tooltip='Close'
              tooltipProps={NOTE_TOOLTIP_PROPS}
              onClick={(event: React.MouseEvent) => {
                close();
                // detail 0 marks a keyboard-activated click (Enter/Space).
                if (event.detail === 0) {
                  restoreFocus();
                  focusAnnotationFallbackIfLost();
                }
              }}
            />
          </header>
          <div className='min-h-x0 grow overflow-y-auto'>
            <AnnotationNotesPanelList
              questionId={questionId}
              editingNoteId={activeEdit?.note.annotation.id}
              onEditNote={
                canEditAnnotations
                  ? (note, row) => openEditor({note, row}, note.regionId)
                  : undefined
              }
            />
          </div>
        </div>
      </aside>
      {/* Outside the aside: a portal's events bubble through the React tree, so
          an Escape for the editor would close the panel too. */}
      {isOpen && activeEdit && (
        <AnnotationEditor
          regionId={activeEdit.note.regionId}
          activeAnnotation={{id: activeEdit.note.annotation.id}}
          anchorEl={activeEdit.row}
          allowUnderline={!isSupplementAnchor(activeEdit.note.annotation.anchor)}
          focusNote
          onClose={() => {
            closeEditor();
            // Deferred past the commit, where the editor returns focus to the pencil;
            // Remove, or a note left empty, takes that row away.
            setTimeout(() => {
              const isFocusLost =
                !document.activeElement || document.activeElement === document.body;
              if (!activeEdit.row.isConnected && isFocusLost) {
                closeButtonRef.current?.focus({preventScroll: true});
              }
            });
          }}
        />
      )}
    </>
  );
}
